import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Trash2, CheckCircle, AlertCircle, ShieldAlert } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

const API = process.env.REACT_APP_BACKEND_URL;

export default function TrackerDeletePage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const [status, setStatus] = useState('confirm'); // confirm | processing | done | error
  const [subscriber, setSubscriber] = useState(null);

  useEffect(() => {
    if (!token) { setStatus('error'); return; }
    fetch(`${API}/api/tracker/me?token=${token}`)
      .then(r => { if (!r.ok) throw new Error(); return r.json(); })
      .then(setSubscriber)
      .catch(() => setStatus('error'));
  }, [token]);

  const handleDelete = async () => {
    setStatus('processing');
    try {
      const res = await fetch(`${API}/api/tracker/delete-account`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token })
      });
      if (!res.ok) throw new Error();
      localStorage.removeItem('tracker_token');
      setStatus('done');
    } catch { setStatus('error'); }
  };

  return (
    <PageLayout>
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4" data-testid="tracker-delete-page">
        <div className="max-w-sm w-full bg-white rounded-2xl border border-slate-200 shadow-lg p-8 text-center">

          {status === 'confirm' && subscriber && (
            <>
              <ShieldAlert className="w-10 h-10 text-red-500 mx-auto mb-4" />
              <h1 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">Delete your account?</h1>
              <p className="text-sm text-slate-500 mb-2">
                This will permanently delete:
              </p>
              <ul className="text-sm text-slate-600 text-left mx-auto max-w-[220px] mb-4 space-y-1">
                <li className="flex items-start gap-2"><Trash2 className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" /> Your tracked countries ({subscriber.tracked_items?.length || 0} items)</li>
                <li className="flex items-start gap-2"><Trash2 className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" /> Your email preferences</li>
                <li className="flex items-start gap-2"><Trash2 className="w-3.5 h-3.5 text-red-400 shrink-0 mt-0.5" /> Your name and email address</li>
              </ul>
              <p className="text-xs text-red-500 font-semibold mb-6">This action cannot be undone.</p>
              <button
                onClick={handleDelete}
                className="w-full py-2.5 rounded-xl bg-red-600 text-white text-sm font-bold hover:bg-red-700 transition-colors mb-3"
                data-testid="confirm-delete-account-btn"
              >
                Yes, delete everything
              </button>
              <Link
                to={`/tracker/my?token=${token}`}
                className="block text-xs font-semibold text-slate-400 hover:text-taxera-blue transition-colors"
              >
                Cancel — keep my account
              </Link>
            </>
          )}

          {status === 'processing' && (
            <>
              <div className="w-10 h-10 rounded-full border-2 border-slate-200 border-t-red-500 animate-spin mx-auto mb-4" />
              <p className="text-sm text-slate-500">Deleting your data...</p>
            </>
          )}

          {status === 'done' && (
            <>
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto mb-4" />
              <h1 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">Account deleted</h1>
              <p className="text-sm text-slate-500 mb-6">
                All your data has been permanently removed. You can always start fresh by tracking a new country.
              </p>
              <Link
                to="/tracker"
                className="inline-block px-6 py-2.5 rounded-xl bg-taxera-blue text-white text-sm font-bold hover:bg-taxera-blue/90 transition-colors"
              >
                Back to Global Tax Tracker
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <AlertCircle className="w-10 h-10 text-red-400 mx-auto mb-4" />
              <h1 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">Something went wrong</h1>
              <p className="text-sm text-slate-500 mb-6">
                {!token ? 'No token provided. This link may be invalid.' : 'The link may have expired or your account was already deleted.'}
              </p>
              <Link
                to="/tracker"
                className="inline-block px-6 py-2.5 rounded-xl bg-taxera-blue text-white text-sm font-bold hover:bg-taxera-blue/90 transition-colors"
              >
                Go to Global Tax Tracker
              </Link>
            </>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
